import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getAccessPolicy, setAccessPolicy, getUserRoleAssignments,
  createUserRoleAssignment, setUserRoleAssignmentActive,
} from "../api/adminApi";
import { getGroups } from "../api/groupRightsApi";
import { UsersArt } from "../components/adminSurface/heroArt";

function fmtDate(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return String(raw); }
}

// Scoped styling — shares the content-governance "AI / newgen" language (indigo→violet gradient,
// soft cards, custom dropdown, toggle switch) with the Holds / Disposition surfaces.
const ACL_STYLES = `
.acl-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.acl-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.acl-hero__icon{ position:relative; width:54px; height:54px; border-radius:15px; flex:0 0 auto;
  background:rgba(255,255,255,.14); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center;
  font-size:27px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.2); }
.acl-hero__body{ position:relative; flex:1; min-width:0; }
.acl-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.acl-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.acl-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.acl-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.acl-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.acl-hero__dot{ width:6px; height:6px; border-radius:50%; }
.acl-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.acl-hero__logo svg{ display:block; height:82px; width:auto; }
.acl-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  transition:background .15s, transform .12s; display:flex; align-items:center; justify-content:center; }
.acl-refresh:hover{ background:rgba(255,255,255,.24); }
.acl-refresh:disabled{ opacity:.6; cursor:not-allowed; }

.acl-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.acl-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.acl-field{ display:flex; flex-direction:column; min-width:0; }
.acl-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.acl-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.acl-input:hover{ border-color:#cbd5e1; }
.acl-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.acl-input::placeholder{ color:#94a3b8; }

.acl-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 22px;
  border-radius:12px; background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed);
  box-shadow:0 8px 20px -4px rgba(109,40,217,.42); transition:filter .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.acl-cta:hover{ filter:brightness(1.06); }
.acl-cta:active{ transform:translateY(1px); }
.acl-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }

.acl-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); }
.acl-row td{ transition:background .12s; }
.acl-row:hover td{ background:#faf7ff !important; }
.acl-chip{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:600;
  background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.acl-status{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px 3px 8px; border-radius:999px;
  font-size:11px; font-weight:700; }
.acl-status__dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.acl-status--on{ background:#dcfce7; color:#15803d; }
.acl-status--off{ background:#f1f5f9; color:#64748b; }
.acl-empty{ padding:56px 40px; text-align:center; }
.acl-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center;
  justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }
@keyframes acl-slide{ from{opacity:0; transform:translateY(-4px);} to{opacity:1; transform:none;} }
.acl-slide{ animation:acl-slide .16s ease; }

.acl-enforce{ display:flex; align-items:center; gap:16px; }
.acl-enforce__txt{ flex:1; min-width:0; }
.acl-enforce__txt b{ font-size:15px; color:#0f172a; }
.acl-enforce__txt p{ margin:3px 0 0; font-size:12.5px; color:#64748b; line-height:1.5; }
.acl-warn{ margin-top:12px; padding:10px 14px; border-radius:10px; background:#fffbeb; border:1px solid #fde68a;
  color:#92400e; font-size:12.5px; display:flex; gap:8px; align-items:flex-start; }

.acl-switch{ position:relative; display:inline-flex; align-items:center; cursor:pointer; user-select:none; flex:0 0 auto; }
.acl-switch input{ position:absolute; opacity:0; width:0; height:0; }
.acl-switch__track{ width:54px; height:30px; border-radius:999px; background:#cbd5e1; position:relative;
  transition:background .18s; }
.acl-switch__track::after{ content:""; position:absolute; top:3px; left:3px; width:24px; height:24px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.32); transition:transform .18s; }
.acl-switch input:checked + .acl-switch__track{ background:linear-gradient(135deg,#6d28d9,#7c3aed); }
.acl-switch input:checked + .acl-switch__track::after{ transform:translateX(24px); }
.acl-switch input:disabled + .acl-switch__track{ opacity:.5; }

.acl-dd{ position:relative; }
.acl-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box;
  padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px;
  font-family:inherit; cursor:pointer; transition:border-color .15s, box-shadow .15s; }
.acl-dd__btn:hover{ border-color:#cbd5e1; }
.acl-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.acl-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.acl-dd__lbl.ph{ color:#94a3b8; }
.acl-dd__chev{ flex:0 0 auto; font-size:11px; color:#94a3b8; transition:transform .15s; }
.acl-dd__btn.open .acl-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.acl-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box;
  background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3);
  padding:6px; max-height:288px; overflow-y:auto; animation:acl-slide .16s ease; }
.acl-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px;
  color:#334155; cursor:pointer; white-space:nowrap; transition:background .1s; }
.acl-dd__opt:hover{ background:#f6f2ff; }
.acl-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
.acl-dd__dot{ width:8px; height:8px; border-radius:3px; background:#cbd5e1; flex:0 0 auto; }
.acl-dd__opt.sel .acl-dd__dot{ background:#7c3aed; }
`;

function Dropdown({ value, onChange, options, placeholder, icon, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="acl-dd" ref={ref}>
      <button type="button" className={`acl-dd__btn${open ? " open" : ""}`} disabled={disabled} onClick={() => setOpen((o) => !o)}>
        {icon && <span style={{ opacity: .75 }}>{icon}</span>}
        <span className={`acl-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="acl-dd__chev">▾</span>
      </button>
      {open && (
        <div className="acl-dd__menu">
          {options.map((o) => (
            <div key={o.value} className={`acl-dd__opt${String(o.value) === String(value) ? " sel" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="acl-dd__dot" />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AccessControlPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [policy, setPolicy] = useState(null);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [userName, setUserName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [filterRole, setFilterRole] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [pol, grps, asn] = await Promise.all([
        getAccessPolicy(token).catch(() => null),
        getGroups(token).catch(() => []),
        getUserRoleAssignments(token, { roleId: filterRole || undefined }).catch(() => []),
      ]);
      setPolicy(pol);
      setRoles(Array.isArray(grps) ? grps : (grps?.groups ?? []));
      setAssignments(Array.isArray(asn) ? asn : []);
    } catch (e) {
      setError(e?.message || t("accessControl.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, filterRole, t]);

  useEffect(() => { load(); }, [load]);

  const toggleEnforce = async (next) => {
    if (saving) return;
    setSaving(true); setError("");
    try {
      const r = await setAccessPolicy(token, { organization: policy?.organization, enforceGrants: next });
      setPolicy((p) => ({ ...(p || {}), ...r }));
    } catch (e) {
      setError(t("accessControl.actionFailed", { error: e?.message || String(e) }));
    } finally {
      setSaving(false);
    }
  };

  const assign = async () => {
    if (assigning) return;
    if (!userName.trim() || !roleId) { setError(t("accessControl.userRoleRequired")); return; }
    setAssigning(true); setError("");
    try {
      await createUserRoleAssignment(token, { userName: userName.trim(), roleId: Number(roleId) });
      setUserName(""); setRoleId("");
      await load();
    } catch (e) {
      setError(t("accessControl.actionFailed", { error: e?.message || String(e) }));
    } finally {
      setAssigning(false);
    }
  };

  const toggleAssignment = async (a) => {
    setError("");
    try {
      await setUserRoleAssignmentActive(token, a.id, !a.isActive);
      await load();
    } catch (e) {
      setError(t("accessControl.actionFailed", { error: e?.message || String(e) }));
    }
  };

  const roleOpts = useMemo(() => roles.map((r) => ({ value: String(r.id ?? r.Id), label: r.name ?? r.Name ?? r.role ?? r.Role ?? String(r.id ?? r.Id) })), [roles]);
  const enforced = !!policy?.enforceGrants;
  const activeCount = useMemo(() => assignments.filter((a) => a.isActive).length, [assignments]);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{ACL_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <div className="acl-hero">
          <div className="acl-hero__glow" />
          <div className="acl-hero__logo"><UsersArt /></div>
          <div className="acl-hero__body">
            <h1 className="acl-hero__title">{t("accessControl.title")}</h1>
            <p className="acl-hero__sub">{t("accessControl.subtitle")}</p>
          </div>
          <div className="acl-hero__stat">
            <b>{activeCount}</b>
            <span><i className="acl-hero__dot" style={{ background: enforced ? "#4ade80" : "#cbd5e1" }} /> {t("accessControl.statusActive")}</span>
          </div>
          <button className="acl-refresh" onClick={load} disabled={loading} title={t("accessControl.refresh")}>
            {loading ? "⟳" : "↺"}
          </button>
        </div>

        {/* Enforcement toggle */}
        <div className="acl-card">
          <div className="acl-enforce">
            <div className="acl-enforce__txt">
              <b>{t("accessControl.enforcement")}</b>
              <p>{t("accessControl.enforcementHint")}</p>
            </div>
            <label className="acl-switch">
              <input type="checkbox" checked={enforced} disabled={saving} onChange={(e) => toggleEnforce(e.target.checked)} />
              <span className="acl-switch__track" />
            </label>
          </div>
          {enforced && (
            <div className="acl-warn acl-slide">
              <span style={{ fontSize: 15 }}>⚠</span>
              <span>{t("accessControl.enforcementWarn")}</span>
            </div>
          )}
        </div>

        {/* Assign user → role */}
        <div className="acl-card">
          <div className="acl-grid">
            <div className="acl-field" style={{ flex: "2 1 240px" }}>
              <label className="acl-lbl">{t("accessControl.userName")}</label>
              <input className="acl-input" type="text" placeholder={t("accessControl.userNamePlaceholder")}
                value={userName} onChange={(e) => setUserName(e.target.value)} />
            </div>
            <div className="acl-field" style={{ flex: "1 1 220px" }}>
              <label className="acl-lbl">{t("accessControl.role")}</label>
              <Dropdown icon="👥" value={roleId} onChange={setRoleId}
                placeholder={roleOpts.length === 0 ? t("accessControl.noRoles") : t("accessControl.selectRole")}
                options={roleOpts} disabled={roleOpts.length === 0} />
            </div>
            <button className="acl-cta" onClick={assign} disabled={assigning || !userName.trim() || !roleId}>
              👤 {assigning ? t("accessControl.assigning") : t("accessControl.assign")}
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="acl-card" style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", padding: "12px 16px" }}>
          <span className="acl-lbl" style={{ margin: 0 }}>{t("accessControl.filterRole")}</span>
          <div style={{ minWidth: 220 }}>
            <Dropdown icon="👥" value={filterRole} onChange={setFilterRole}
              placeholder={t("accessControl.allRoles")}
              options={[{ value: "", label: t("accessControl.allRoles") }, ...roleOpts]} />
          </div>
          {!loading && <span className="acl-chip" style={{ marginLeft: "auto" }}>👤 {assignments.length}</span>}
        </div>

        {error && (
          <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
            padding: "11px 16px", marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚠</span> {error}
          </div>
        )}

        <div className="acl-tablecard" style={{ overflowX: "auto" }}>
          {assignments.length === 0 && !loading ? (
            <div className="acl-empty">
              <div className="acl-empty__ring">🛂</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{t("accessControl.noAssignments")}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{t("accessControl.noAssignmentsHint")}</div>
            </div>
          ) : (
            <table className="logs-table" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 18 }}>{t("accessControl.colUser")}</th>
                  <th style={{ width: 200 }}>{t("accessControl.colRole")}</th>
                  <th style={{ width: 120 }}>{t("accessControl.colStatus")}</th>
                  <th style={{ width: 180 }}>{t("accessControl.colAssigned")}</th>
                  <th style={{ width: 130 }}></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr className="acl-row" key={a.id}>
                    <td style={{ paddingLeft: 18, fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{a.userName}</td>
                    <td><span className="acl-chip">{a.roleName ?? a.roleId}</span></td>
                    <td>
                      <span className={`acl-status ${a.isActive ? "acl-status--on" : "acl-status--off"}`}>
                        <span className="acl-status__dot" />
                        {a.isActive ? t("accessControl.statusActive") : t("accessControl.statusInactive")}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: "#64748b" }}>
                      {fmtDate(a.createdAt)}{a.createdBy ? ` ${t("accessControl.by", { user: a.createdBy })}` : ""}
                    </td>
                    <td>
                      <button className="link-btn" style={{ fontSize: 13, color: a.isActive ? "#64748b" : "#1d4ed8" }} onClick={() => toggleAssignment(a)}>
                        {a.isActive ? t("accessControl.deactivate") : t("accessControl.reactivate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
